import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { EchoflicksSessionJourney } from "../types";
import { buildStoryboardFirstAudit } from "./storyboardFirstAudit";

const label = (value: string | null) =>
  value ? value.replaceAll("_", " ") : "unavailable";

export function StoryboardFirstAuditCard({
  journey,
  isLoading = false,
}: {
  journey?: EchoflicksSessionJourney;
  isLoading?: boolean;
}) {
  const audit = buildStoryboardFirstAudit(journey);
  if (isLoading) return null;
  return (
    <Card aria-label="Storyboard-first audit">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Storyboard-first audit</CardTitle>
          <Badge
            variant={
              audit.finalDisposition === "failed"
                ? "destructive"
                : audit.finalDisposition === "accepted"
                  ? "secondary"
                  : "outline"
            }
          >
            {audit.available
              ? label(audit.finalDisposition ?? audit.decision)
              : "Legacy / off"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!audit.available ? (
          <p className="text-muted-foreground">
            No storyboard-first receipts were emitted. This is normal for
            legacy, policy-off and ineligible Echoes.
          </p>
        ) : (
          <>
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">Eligibility</dt>
                <dd>{label(audit.decision)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Board</dt>
                <dd>
                  {audit.boardId ?? "unavailable"}
                  {audit.revision ? ` · r${audit.revision}` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Required beats</dt>
                <dd>
                  {audit.coveredBeats ?? "?"} / {audit.requiredBeats ?? "?"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Panel attempts</dt>
                <dd>{audit.attempts}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Selected route</dt>
                <dd>
                  {audit.selectedProvider ?? "unavailable"}
                  {audit.selectedVariant ? ` · ${audit.selectedVariant}` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Frozen release</dt>
                <dd className="break-all">
                  {audit.configReleaseId ?? "unavailable"}
                </dd>
              </div>
            </dl>
            {audit.reason ? (
              <p className="text-muted-foreground">{audit.reason}</p>
            ) : null}
            {audit.finalIssueCodes.length ? (
              <p role="alert" className="text-destructive">
                Final issues: {audit.finalIssueCodes.map(label).join(", ")}
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
